/**
 * /trace-search · Trace 检索页（W4）· 补 trace 检索命门（此前无列表端点）。
 * 按 商户/outcome/降级 过滤 → 结果表 → 点行看完整 TraceDetail（复用 M01 组件）。
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { TraceDetail } from "@/components/m01/trace-detail";
import { PageHeader, type PageHelp } from "@/components/shared/page-help";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Drawer } from "@/components/ui/drawer";
import { Select } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { type TraceListItem, useTraceList } from "@/lib/api/trace";

export const Route = createFileRoute("/trace-search")({
	component: TraceSearchPage,
});

const DEFAULT_MERCHANT_ID = "mer_devcons";
const OUTCOMES = ["", "success", "partial", "failed", "handoff"] as const;

function outcomeTone(outcome: string): string {
	const base =
		"inline-flex w-fit rounded-md border border-surface-border px-2 py-0.5 text-xs font-medium bg-surface-card";
	if (outcome === "success") return `${base} text-brand-success`;
	if (outcome === "partial") return `${base} text-brand-warning`;
	if (outcome === "failed") return `${base} text-brand-danger`;
	return `${base} text-text-secondary`; // handoff / other
}

function formatTime(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return new Intl.DateTimeFormat("zh-CN", {
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	}).format(d);
}

function TraceSearchPage() {
	const [merchantId, setMerchantId] = useState(DEFAULT_MERCHANT_ID);
	const [outcome, setOutcome] = useState<string>("");
	const [degradedOnly, setDegradedOnly] = useState(false);
	const [selected, setSelected] = useState<string | null>(null);

	const traces = useTraceList({
		merchant_id: merchantId.trim() || undefined,
		final_outcome: outcome || undefined,
		degraded_only: degradedOnly,
		limit: 50,
	});
	const items = traces.data?.items ?? [];

	return (
		<div className="mx-auto flex max-w-7xl flex-col gap-5" data-testid="trace-search-shell">
			<PageHeader
				moduleId="T8"
				title="Trace 检索"
				description="按商户、结果、是否降级筛选最近的对话 trace，点开看完整链路详情。"
				help={traceSearchHelp}
			/>

			<Card>
				<CardHeader>
					<CardTitle className="text-lg">筛选</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid gap-3 md:grid-cols-[200px_200px_auto]">
						<label className="grid gap-1 text-sm">
							<span className="text-xs text-text-muted">商户 ID</span>
							<input
								value={merchantId}
								onChange={(event) => setMerchantId(event.target.value)}
								className="h-9 rounded-md border border-surface-border bg-surface-background px-3 font-mono text-sm"
								data-testid="trace-search-merchant"
							/>
						</label>
						<div className="grid gap-1 text-sm">
							<span className="text-xs text-text-muted">最终结果</span>
							<Select
								className="w-full"
								aria-label="最终结果"
								value={outcome}
								onChange={(event) => setOutcome(event.target.value)}
								data-testid="trace-search-outcome"
							>
								{OUTCOMES.map((o) => (
									<option key={o || "all"} value={o}>
										{o === "" ? "全部" : o}
									</option>
								))}
							</Select>
						</div>
						<span className="flex items-end gap-2 text-sm">
							<Switch
								checked={degradedOnly}
								onCheckedChange={setDegradedOnly}
								data-testid="trace-search-degraded"
							/>
							<span className="pb-2 text-text-secondary">只看有降级的</span>
						</span>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						结果{traces.data ? ` · 共 ${traces.data.total} 条（显示前 ${items.length}）` : ""}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{traces.isLoading ? (
						<div
							className="p-6 text-center text-sm text-text-muted"
							data-testid="trace-search-loading"
						>
							加载中…
						</div>
					) : traces.isError ? (
						<div
							className="p-6 text-center text-sm text-brand-danger"
							data-testid="trace-search-error"
						>
							加载失败，请重试。
						</div>
					) : items.length === 0 ? (
						<div
							className="rounded-md border border-dashed border-surface-border p-8 text-center text-sm text-text-muted"
							data-testid="trace-search-empty"
						>
							没有符合条件的 trace。
						</div>
					) : (
						<div className="overflow-x-auto">
							<table className="w-full border-collapse text-sm" data-testid="trace-search-table">
								<thead className="bg-surface-subtle text-left">
									<tr>
										<th className="px-3 py-2 font-medium">时间</th>
										<th className="px-3 py-2 font-medium">意图</th>
										<th className="px-3 py-2 font-medium">结果</th>
										<th className="px-3 py-2 font-medium">降级模块</th>
										<th className="px-3 py-2 font-medium">延迟</th>
										<th className="px-3 py-2 font-medium" />
									</tr>
								</thead>
								<tbody>
									{items.map((item: TraceListItem) => (
										<tr
											key={item.trace_id}
											className="border-t border-surface-border"
											data-testid={`trace-search-row-${item.trace_id}`}
										>
											<td className="px-3 py-2 font-mono text-xs">{formatTime(item.timestamp)}</td>
											<td className="px-3 py-2">{item.intent ?? "—"}</td>
											<td className="px-3 py-2">
												<span className={outcomeTone(item.final_outcome)}>
													{item.final_outcome}
												</span>
											</td>
											<td className="px-3 py-2">
												{item.degraded_modules.length === 0 ? (
													<span className="text-text-muted">—</span>
												) : (
													<span className="font-mono text-xs text-brand-danger">
														{item.degraded_modules.join(", ")}
													</span>
												)}
											</td>
											<td className="px-3 py-2 font-mono text-xs">{item.total_latency_ms}ms</td>
											<td className="px-3 py-2">
												<Button
													type="button"
													variant="outline"
													size="sm"
													onClick={() => setSelected(item.trace_id)}
													data-testid={`trace-search-open-${item.trace_id}`}
												>
													详情
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</CardContent>
			</Card>

			<Drawer
				open={selected !== null}
				onClose={() => setSelected(null)}
				title="Trace 详情"
				width="min(980px,100vw)"
				testId="trace-search-detail-drawer"
			>
				{selected ? <TraceDetail traceId={selected} /> : null}
			</Drawer>
		</div>
	);
}

const traceSearchHelp: PageHelp = {
	title: "Trace 检索说明",
	description: "补齐了之前缺失的 trace 列表/检索能力（此前没有列表端点）。",
	sections: [
		{
			title: "怎么用",
			items: [
				"按商户、最终结果、是否降级筛选最近 trace。",
				"点「详情」看完整链路（intent / phase / grounding / 降级原因）。",
				"“只看有降级的”用于快速定位 degraded_modules 非空的对话。",
			],
		},
	],
};
